import appInsights from '@/app/components/AppInsights';
import type { AccountInfo } from '@azure/msal-browser';

/**
 * Authentication telemetry service for tracking token expiration and re-authentication patterns
 */
export class AuthTelemetryService {
  private static instance: AuthTelemetryService;
  private sessionStartTime: number = Date.now();
  private lastTokenRefresh: number = Date.now();
  private tokenRefreshCount: number = 0;
  private silentAuthFailureCount: number = 0;
  private interactiveAuthCount: number = 0;

  private constructor() {}

  public static getInstance(): AuthTelemetryService {
    if (!AuthTelemetryService.instance) {
      AuthTelemetryService.instance = new AuthTelemetryService();
    }
    return AuthTelemetryService.instance;
  }

  /**
   * Track when a user session starts (successful login)
   */
  public trackSessionStart(account: AccountInfo, loginMethod: 'silent' | 'popup' | 'redirect'): void {
    this.sessionStartTime = Date.now();
    this.tokenRefreshCount = 0;
    this.silentAuthFailureCount = 0;
    this.interactiveAuthCount = loginMethod === 'silent' ? 0 : 1;

    appInsights.trackEvent({
      name: 'Auth_SessionStart',
      properties: {
        loginMethod,
        username: account.username,
        tenantId: account.tenantId,
        accountType: account.homeAccountId ? 'existing' : 'new',
        timestamp: new Date().toISOString()
      },
      measurements: {
        sessionStartTime: this.sessionStartTime
      }
    });

    // Track as custom metric for easier querying
    appInsights.trackMetric({
      name: 'Auth_SessionsStarted',
      average: 1,
      properties: {
        loginMethod,
        accountType: account.homeAccountId ? 'existing' : 'new'
      }
    });
  }

  /**
   * Track successful token refresh
   */
  public trackTokenRefreshSuccess(refreshMethod: 'automatic' | 'manual' | 'activity-triggered'): void {
    const now = Date.now();
    const timeSinceLastRefresh = now - this.lastTokenRefresh;
    const timeSinceSessionStart = now - this.sessionStartTime;
    
    this.lastTokenRefresh = now;
    this.tokenRefreshCount++;

    appInsights.trackEvent({
      name: 'Auth_TokenRefreshSuccess',
      properties: {
        refreshMethod,
        timestamp: new Date().toISOString()
      },
      measurements: {
        timeSinceLastRefresh,
        timeSinceSessionStart,
        totalRefreshCount: this.tokenRefreshCount
      }
    });

    // Track refresh frequency as a metric
    appInsights.trackMetric({
      name: 'Auth_TokenRefreshInterval',
      average: timeSinceLastRefresh,
      properties: {
        refreshMethod
      }
    });
  }

  /**
   * Track failed token refresh attempts
   */
  public trackTokenRefreshFailure(error: any, refreshMethod: 'automatic' | 'manual' | 'activity-triggered'): void {
    const now = Date.now();
    const timeSinceLastRefresh = now - this.lastTokenRefresh;
    const timeSinceSessionStart = now - this.sessionStartTime;

    appInsights.trackEvent({
      name: 'Auth_TokenRefreshFailure',
      properties: {
        refreshMethod,
        errorCode: error?.errorCode || 'unknown',
        errorMessage: error?.errorMessage || error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      },
      measurements: {
        timeSinceLastRefresh,
        timeSinceSessionStart,
        totalRefreshCount: this.tokenRefreshCount
      }
    });

    // Track as exception for alerting
    appInsights.trackException({
      exception: new Error(`Token refresh failed: ${error?.errorMessage || error?.message}`),
      properties: {
        refreshMethod,
        errorCode: error?.errorCode || 'unknown'
      }
    });
  }

  /**
   * Track silent authentication failures
   */
  public trackSilentAuthFailure(error: any, fallbackAction: 'popup' | 'redirect' | 'none'): void {
    this.silentAuthFailureCount++;
    const timeSinceSessionStart = Date.now() - this.sessionStartTime;

    appInsights.trackEvent({
      name: 'Auth_SilentAuthFailure',
      properties: {
        errorCode: error?.errorCode || 'unknown',
        errorMessage: error?.errorMessage || error?.message || 'Unknown error',
        fallbackAction,
        timestamp: new Date().toISOString()
      },
      measurements: {
        timeSinceSessionStart,
        silentFailureCount: this.silentAuthFailureCount
      }
    });

    // Track silent auth failure rate
    appInsights.trackMetric({
      name: 'Auth_SilentAuthFailureRate',
      average: this.silentAuthFailureCount,
      properties: {
        errorCode: error?.errorCode || 'unknown',
        fallbackAction
      }
    });
  }

  /**
   * Track interactive authentication (popup/redirect)
   */
  public trackInteractiveAuth(method: 'popup' | 'redirect', reason: 'silent_failed' | 'user_initiated' | 'token_expired'): void {
    this.interactiveAuthCount++;
    const timeSinceSessionStart = Date.now() - this.sessionStartTime;

    appInsights.trackEvent({
      name: 'Auth_InteractiveAuth',
      properties: {
        method,
        reason,
        timestamp: new Date().toISOString()
      },
      measurements: {
        timeSinceSessionStart,
        interactiveAuthCount: this.interactiveAuthCount
      }
    });

    // Track interactive auth frequency
    appInsights.trackMetric({
      name: 'Auth_InteractiveAuthFrequency',
      average: this.interactiveAuthCount,
      properties: {
        method,
        reason
      }
    });
  }

  /**
   * Track session duration when user logs out or session ends
   */
  public trackSessionEnd(reason: 'user_logout' | 'token_expired' | 'error' | 'page_unload'): void {
    const sessionDuration = Date.now() - this.sessionStartTime;
    const avgRefreshInterval = this.tokenRefreshCount > 0 ? sessionDuration / this.tokenRefreshCount : 0;

    appInsights.trackEvent({
      name: 'Auth_SessionEnd',
      properties: {
        reason,
        timestamp: new Date().toISOString()
      },
      measurements: {
        sessionDuration,
        totalTokenRefreshes: this.tokenRefreshCount,
        silentAuthFailures: this.silentAuthFailureCount,
        interactiveAuthCount: this.interactiveAuthCount,
        avgRefreshInterval
      }
    });

    // Track session duration as a metric
    appInsights.trackMetric({
      name: 'Auth_SessionDuration',
      average: sessionDuration,
      properties: {
        reason,
        refreshCount: this.tokenRefreshCount.toString()
      }
    });

    // Reset counters for next session
    this.resetCounters();
  }

  /**
   * Track token expiration events
   */
  public trackTokenExpiration(tokenType: 'access' | 'id' | 'refresh', timeToExpiry: number): void {
    appInsights.trackEvent({
      name: 'Auth_TokenExpiration',
      properties: {
        tokenType,
        timestamp: new Date().toISOString()
      },
      measurements: {
        timeToExpiry,
        timeSinceSessionStart: Date.now() - this.sessionStartTime
      }
    });

    // Track token lifetime as a metric
    appInsights.trackMetric({
      name: 'Auth_TokenLifetime',
      average: timeToExpiry,
      properties: {
        tokenType
      }
    });
  }

  /**
   * Track user activity patterns for correlation with auth events
   */
  public trackUserActivity(activityType: 'mouse' | 'keyboard' | 'focus' | 'visibility'): void {
    const timeSinceSessionStart = Date.now() - this.sessionStartTime;
    const timeSinceLastRefresh = Date.now() - this.lastTokenRefresh;

    // Only track periodically to avoid spam
    if (Math.random() < 0.01) { // 1% sampling
      appInsights.trackEvent({
        name: 'Auth_UserActivity',
        properties: {
          activityType,
          timestamp: new Date().toISOString()
        },
        measurements: {
          timeSinceSessionStart,
          timeSinceLastRefresh
        }
      });
    }
  }

  /**
   * Generate and track authentication health summary
   */
  public trackAuthHealthSummary(): void {
    const timeSinceSessionStart = Date.now() - this.sessionStartTime;
    const refreshSuccessRate = this.tokenRefreshCount > 0 ? 
      ((this.tokenRefreshCount - this.silentAuthFailureCount) / this.tokenRefreshCount) * 100 : 100;

    appInsights.trackEvent({
      name: 'Auth_HealthSummary',
      properties: {
        timestamp: new Date().toISOString()
      },
      measurements: {
        sessionDuration: timeSinceSessionStart,
        tokenRefreshCount: this.tokenRefreshCount,
        silentAuthFailureCount: this.silentAuthFailureCount,
        interactiveAuthCount: this.interactiveAuthCount,
        refreshSuccessRate
      }
    });
  }

  private resetCounters(): void {
    this.tokenRefreshCount = 0;
    this.silentAuthFailureCount = 0;
    this.interactiveAuthCount = 0;
  }
}

// Export singleton instance
export const authTelemetry = AuthTelemetryService.getInstance();
